import {
  formatPolitician,
  preparePoliticians,
} from "@/controllers/politician.js";
import { prepareProjects } from "@/controllers/project.js";
import { preparePublications } from "@/controllers/publicacion.js";
import { prepareVotings } from "@/controllers/voting.js";
import { formatDateFromSF } from "@/helpers";
import {
  crearSolicitud,
  establecimiento,
  getEstablecimientos,
  politico,
  politicos,
  proyecto,
  proyectos,
  publicacion,
  publicaciones,
  votacion,
  votaciones,
} from "@/services/base-api";
import { useAuthorizationStore } from "@/stores";
import { defineStore } from "pinia";

const actions = {
  async getAccounts() {
    if (useAuthorizationStore().role === "desconocido") {
      return;
    }
    this.loadingAccounts = true;
    let data = { table: {}, form: {} };
    if (!this.table.accounts) {
      let response;
      try {
        response = await getEstablecimientos();
      } catch (e) {
        console.error(e);
      }
      if (response?.data) {
        if (response.data.table.establecimientos) {
          this.table.accounts = Object.fromEntries(
            Object.values(response.data.table.establecimientos)
              .sort((a, b) => a.Name.localeCompare(b.Name))
              .map((account) => [account.Id, account]),
          );
          this.table.comissions = response.data.table.comisiones;
        }
        this.schema = response.data.schema || {};
        this.choices = response.data.form;
      }
    }
    this.loadingAccounts = false;
    return data;
  },
  async getPoliticians(filters = {}) {
    this.loadingPoliticians = true;
    let response;
    try {
      response = await politicos(filters);
    } finally {
      if (response?.data) {
        this.table.politicians = preparePoliticians(response.data.table);
        this.filters.politicians = JSON.stringify(filters);
      }
      this.loadingPoliticians = false;
    }
  },
  async getProjects(filters = {}) {
    this.loadingProjects = true;
    let response;
    try {
      response = await proyectos(filters);
    } finally {
      if (response?.data) {
        this.table.projects = prepareProjects(response.data.table.proyectos);
        this.filters.projects = JSON.stringify(filters);
      }
      this.loadingProjects = false;
    }
  },
  async getVotings(filters = {}) {
    this.loadingVotings = true;
    let response;
    try {
      response = await votaciones(filters);
    } finally {
      if (response?.data) {
        this.table.votings = prepareVotings(response.data.table.votaciones);
        this.filters.votings = JSON.stringify(filters);
      }
      this.loadingVotings = false;
    }
  },
  async getPublications(filters = {}) {
    this.loadingPublications = true;
    let response;
    try {
      response = await publicaciones(filters);
    } finally {
      if (response?.data) {
        this.table.publications = preparePublications(
          response.data.table.publicaciones,
        );
        this.filters.publications = JSON.stringify(filters);
      }
      this.loadingPublications = false;
    }
  },
  async getAccount(id = "") {
    this.loadingAccount = true;
    let response;
    try {
      response = await establecimiento(id);
    } finally {
      if (response?.data) {
        response.data?.establecimiento && this.prepareAccount(response.data);
      }
      this.loadingAccount = false;
    }
  },
  prepareAccount(account) {
    const index = account.establecimiento.Id;
    this.rows.accounts[index] = {
      ...(this.accounts[index] || {}),
      ...(this.rows.accounts[index] || {}),
      ...account,
    };
  },
  async getPolitician(id = "") {
    this.loadingPolitician = true;
    let response;
    try {
      response = await politico(id);
    } finally {
      const data = response?.data?.contacto
        ? response.data
        : { contacto: { Id: id } };
      this.preparePolitician(data);
      this.loadingPolitician = false;
    }
  },
  preparePolitician(politician) {
    const index = politician.contacto.Id;
    this.rows.politicians[index] = {
      ...(this.politicians[index] || {}),
      ...(this.rows.politicians[index] || {}),
      ...politician,
      cadeira: {
        ...(politician.cadeira || {}),
        ...formatPolitician(politician.cadeira),
      },
    };
  },
  async getProject(id = "") {
    this.loadingProject = true;
    let response;
    try {
      response = await proyecto(id);
    } finally {
      const data = response?.data?.proyecto
        ? response.data
        : { proyecto: { Id: id } };
      this.prepareProject(data);
      this.loadingProject = false;
    }
  },
  prepareProject(data) {
    const id = data.proyecto.Id;
    this.rows.projects[id] = {
      ...(this.rows.projects[id] || {}),
      ...data,
      proyecto: {
        ...(this.projects[id] || {}),
        ...data.proyecto,
      },
    };
  },
  async getVoting(id = "") {
    this.loadingVoting = true;
    let response;
    try {
      response = await votacion(id);
    } finally {
      const data = response?.data?.votacion
        ? response.data.votacion
        : { votacion: { Id: id } };
      this.prepareVoting(data);
      this.prepareVotes(response.data.votacion.Id, response.data.votos);
      this.loadingVoting = false;
    }
  },
  prepareVoting(voting) {
    const date = formatDateFromSF(voting.Fecha__c);
    this.rows.votings[voting.Id] = {
      ...(this.votings[voting.Id] || {}),
      ...(this.rows.votings[voting.Id] || {}),
      ...voting,
      date,
      timestamp: date?.getTime() || 0,
    };
  },
  prepareVotes(votingId, votes) {
    let results = {};
    this.rows.votes[votingId] = {
      values: votes.map((row) => {
        if (row.titular) {
          row.titular_Politico__r_Id = row.titular.Politico__c;
          row.titular_Politico__r_Name = row.titular.Politico__r?.Name;
          row.titular_Partido__r_Name = row.titular.Partido__r?.Name;
        }
        if (row.suplente) {
          row.suplente_Politico__r_Id = row.suplente.Politico__c;
          row.suplente_Politico__r_Name = row.suplente.Politico__r?.Name;
          row.suplente_Partido__r_Name = row.suplente.Partido__r?.Name;
        }
        if (row.voto) {
          row.vote = row.voto.Tipo__c;
          if (!results[row.vote]) {
            results[row.vote] = 0;
          }
          results[row.vote]++;
          row.type = row.voto.SubTipo__c;
          row.id = row.voto.Id;
        } else {
          row.vote = null;
          row.type = null;
          row.id = null;
        }
        row.isSubstitute = row.hechoPorSuplente;
        if (row.isSubstitute) {
          row.politicianName = row.suplente_Politico__r_Name;
          row.partyName = row.suplente_Partido__r_Name;
          row.curriculumId = row.suplente.Id;
          row.politicianId = row.suplente_Politico__r_Id;
        } else {
          row.politicianName = row.titular_Politico__r_Name;
          row.partyName = row.titular_Partido__r_Name;
          row.curriculumId = row.titular?.Id;
          row.politicianId = row.titular_Politico__r_Id;
        }
        row.politicos = [
          { title: row.titular_Politico__r_Name, value: false },
          { title: row.suplente_Politico__r_Name, value: true },
        ];
        row.cadeira_Id = row.cadeira.Id;
        row.crear = "votos";
        row.votacionId = votingId;
        row.rowId = row.cadeira.Mandato__c;
        return row;
      }),
    };
  },
  async getPublication(id = "") {
    this.loadingPublication = true;
    let response;
    try {
      response = await publicacion(id);
    } finally {
      const data = response?.data?.publicacion
        ? response.data.publicacion
        : { publicacion: { Id: id } };
      this.preparePublication(data);
      this.loadingPublication = false;
    }
  },
  preparePublication(publication) {
    const date = formatDateFromSF(publication.Fecha__c);
    this.rows.publications[publication.Id] = {
      ...(this.publications[publication.Id] || {}),
      ...(this.rows.publications[publication.Id] || {}),
      ...publication,
      date,
      timestamp: date?.getTime() || 0,
    };
  },
  async createRequest(payload = {}) {
    this.loadingCreation = true;
    try {
      const response = await crearSolicitud(payload);
      return response.data;
    } finally {
      this.loadingCreation = false;
    }
  },
};

export const useDatabaseStore = defineStore("database", {
  state: () => ({
    table: {
      accounts: null,
      comissions: null,
      politicians: null,
      projects: null,
      votings: null,
      publications: null,
    },
    rows: {
      accounts: {},
      comissions: {},
      politicians: {},
      projects: {},
      votings: {},
      votes: {},
      publications: {},
    },
    filters: {
      politicians: null,
      projects: null,
      votings: null,
      publications: null,
    },
    schema: null,
    choices: null,
    loadingAccounts: false,
    loadingProjects: false,
    loadingPoliticians: false,
    loadingVotings: false,
    loadingPublications: false,
    loadingAccount: false,
    loadingPolitician: false,
    loadingProject: false,
    loadingVoting: false,
    loadingPublication: false,
    loadingCreation: false,
    loadingChoices: false,
  }),
  getters: {
    prevAccount: (context) => (Id) => context.accounts[Id] || {},
    prevPolitician: (context) => (Id) => context.politicians[Id] || {},
    prevProject: (context) => (Id) => context.projects[Id] || {},
    prevVoting: (context) => (Id) => context.votings[Id] || {},
    prevPublication: (context) => (Id) => context.publications[Id] || {},

    tableComissions: (context) => (account) => {
      const comissions = context.table.comissions || {};
      const tableComissions = Object.values(comissions).filter(
        (comission) => comission.Parent.Id === account.Id,
      );
      return tableComissions;
    },
    tableAccounts: (context) => Object.values(context.table.accounts || {}),
    accounts: (context) => context.table.accounts || {},
    politicians: (context) => context.table.politicians || {},
    projects: (context) => context.table.projects || {},
    votings: (context) => context.table.votings || {},
    publications: (context) => context.table.publications || {},

    account: (context) => (Id) => context.rows.accounts[Id] || {},
    politician: (context) => (Id) => context.rows.politicians[Id] || {},
    project: (context) => (Id) => context.rows.projects[Id] || {},
    voting: (context) => (Id) => context.rows.votings[Id] || {},
    votingVotes: (context) => (Id) => context.rows.votes[Id] || { values: [] },
    publication: (context) => (Id) => context.rows.publications[Id] || {},

    getAccountOrComission: (context) => (Id) =>
      context.table.accounts?.[Id] ||
      context.rows.accounts?.[Id] ||
      context.table.comissions?.[Id] ||
      context.rows.comissions?.[Id] || { Name: "" },

    choicesProjectTypes: (context) =>
      context.choices?.proyectos?.tiposDeRegistro || [],
    projectPositions: (context) => (projectType) => {
      const positions = context.choices?.proyectos?.posiciones || {};
      return projectType in positions ? positions[projectType] : [];
    },
    projectSubjects: (context) => context.choices?.proyectos?.temas || [],
    projectChoiceTypes: (context) => (projectType) => {
      const types = context.choices?.proyectos?.tipos || {};
      return projectType in types ? types[projectType] : [];
    },
    choicesVotingTypes: (context) =>
      context.choices?.votaciones?.tiposDeRegistro || [],
    votingChoiceTypes: (context) => context.choices?.votaciones?.tipos || [],
    votingStatus: (context) => context.choices?.votaciones?.estatus || [],
    publicationSubjects: (context) =>
      context.choices?.publicaciones?.temas || [],
    publicationMedia: (context) => context.choices?.publicaciones?.medios || [],
    publicationStatus: (context) =>
      context.choices?.publicaciones?.estatus || [],
    periodNumbers: (context) => context.choices?.periodos || [],
  },
  actions,
});
